if (!customElements.get('zapiet-picker')) {
  customElements.define(
    'zapiet-picker',
    class ZapietPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.#initCheckoutGate();
        this.#injectStylesWhenReady();
        this.#keepAutocompleteInView();
      }

      // Waits (up to 20s) for the Zapiet app embed to expose window.ZapietEvent.
      #whenZapietReady(callback) {
        if (window.ZapietEvent) {
          callback();
          return;
        }
        const start = Date.now();
        const poll = window.setInterval(() => {
          if (window.ZapietEvent) {
            window.clearInterval(poll);
            callback();
          } else if (Date.now() - start > 20000) {
            window.clearInterval(poll);
          }
        }, 100);
      }

      // Shares the checkout button's data-awaiting-checkout-conditions counter
      // with terms.js and wonderly-flexi-box.js: this picker holds exactly one
      // count until Zapiet reports checkoutEnabled. The button is resolved from
      // the nearest [data-cart-checkout-scope] so the same element works on the
      // cart page footer and inside the cart drawer.
      #initCheckoutGate() {
        const scope = this.closest('[data-cart-checkout-scope]') || document;
        const checkoutBtn = scope.querySelector('[data-cart-checkout]');
        if (!checkoutBtn) return;

        let held = true;
        checkoutBtn.dataset.awaitingCheckoutConditions = (+checkoutBtn.dataset.awaitingCheckoutConditions || 0) + 1;
        checkoutBtn.disabled = true;

        // checkoutEnabled can fire more than once (method/date changes) —
        // release the hold once and never drive the counter negative.
        const release = () => {
          if (!held) return;
          held = false;
          const remaining = Math.max(0, (+checkoutBtn.dataset.awaitingCheckoutConditions || 0) - 1);
          checkoutBtn.dataset.awaitingCheckoutConditions = remaining;
          if (remaining === 0) checkoutBtn.disabled = false;
        };
        this.#whenZapietReady(() => {
          window.ZapietEvent.listen('checkoutEnabled', release);
          // Zapiet exposes its current state as a boolean global — covers a
          // checkoutEnabled that fired before this listener was bound.
          if (window.ZapietCheckoutEnabled === true) release();
        });
      }

      // Cart drawer only. The drawer footer scrolls internally on short
      // viewports, and a scroll container clips its absolutely positioned
      // descendants — Zapiet's address autocomplete opens upward and lost its
      // top at the footer's edge. wonderly-zapiet-cart.css makes the list
      // position: fixed (containing block = the transformed .drawer__inner, so
      // the footer can't clip it); this anchors the list to its input and caps
      // its height to the space above, so it always stays inside the panel.
      #keepAutocompleteInView() {
        const panel = this.closest('cart-drawer .drawer__inner');
        if (!panel) return;

        const openList = () => this.querySelector('.Zapiet-Autocomplete__List--open');

        const position = () => {
          const list = openList();
          if (!list) return;
          const anchor = list.parentElement.getBoundingClientRect();
          // Fixed boxes resolve against the panel while it is transformed
          // (Dawn's slide-in); against the viewport otherwise.
          const box =
            getComputedStyle(panel).transform === 'none'
              ? { top: 0, left: 0, bottom: window.innerHeight }
              : panel.getBoundingClientRect();
          // Open upward from the input, but no higher than the drawer header.
          const ceiling = panel.querySelector('cart-drawer-items')?.getBoundingClientRect().top ?? box.top;
          const gap = 4;
          this.style.setProperty('--wonderly-zapiet-list-left', `${anchor.left - box.left}px`);
          this.style.setProperty('--wonderly-zapiet-list-width', `${anchor.width}px`);
          this.style.setProperty('--wonderly-zapiet-list-bottom', `${box.bottom - anchor.top + gap}px`);
          this.style.setProperty('--wonderly-zapiet-list-space', `${Math.max(0, anchor.top - ceiling - gap * 2)}px`);
        };
        // Zapiet keeps adjusting its layout after the class flips, so measure
        // now and again once that layout has settled.
        const reposition = () => {
          position();
          window.requestAnimationFrame(position);
        };

        // Zapiet toggles Zapiet-Autocomplete__List--open (and may re-create
        // the list) as the buyer types.
        new MutationObserver(reposition).observe(this, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          subtree: true,
        });
        // Anything that resizes the widget (messages, method tabs) moves the input.
        if ('ResizeObserver' in window) new ResizeObserver(reposition).observe(this);
        // The input moves when the footer scrolls or the viewport resizes.
        this.closest('.drawer__footer')?.addEventListener('scroll', position, { passive: true });
        window.addEventListener('resize', position, { passive: true });
      }

      #injectStylesWhenReady() {
        const href = this.dataset.zapietStyles;
        if (!href) return;

        const inject = () => {
          let link = document.getElementById('wonderly-zapiet-cart-styles');

          if (!link) {
            link = document.createElement('link');
            link.id = 'wonderly-zapiet-cart-styles';
            link.rel = 'stylesheet';
            link.href = href;
          }

          document.head.appendChild(link);
        };

        this.#whenZapietReady(() => {
          window.ZapietEvent.listen('widget_loaded', inject);
          window.ZapietEvent.listen('selected_method', inject);
        });

        const observer = new MutationObserver(() => {
          if (this.querySelector('#storePickupApp .checkoutMethod')) {
            inject();
          }
        });

        observer.observe(this, { childList: true, subtree: true });

        if (this.querySelector('#storePickupApp .checkoutMethod')) {
          inject();
        }
      }
    }
  );
}
